import _ from 'lodash';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { EmbodimentHistoryModel } from '../../models/embodiment-history-model';
import type { EmbodimentHistoryService } from '../embodiment-history-service';
import type { EmbodimentHistoryState, EmbodimentItem } from '../../types';

const MAX_HISTORY_COUNT = 5;

@injectable()
export class EmbodimentHistoryServiceImpl implements EmbodimentHistoryService {
    private _onModelDataChangeEmitter = new Emitter<EmbodimentHistoryState>();
    public readonly onStateChange = this._onModelDataChangeEmitter.event;
    private _onToggleHistoryEmitter = new Emitter<EmbodimentItem>();
    public readonly onToggleHistory = this._onToggleHistoryEmitter.event;

    constructor(
        @inject(EmbodimentHistoryModel)
        private _model: EmbodimentHistoryModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange(): void {
        this._onModelDataChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): EmbodimentHistoryState {
        return this._model.modelData;
    }

    private _updateModelData(value: Partial<EmbodimentHistoryState>): void {
        this._model.update(state => ({
            ...state,
            ...value,
        }));
    }

    private _isIndexOutOfBounds(index: number): boolean {
        const { history } = this.getModelData();
        return index < 0 || index >= history.length;
    }

    public pushHistory(embodiment: EmbodimentItem): void {
        const modelData = this.getModelData();
        const newHistoryList = [...modelData.history];

        if (newHistoryList.length >= MAX_HISTORY_COUNT) {
            newHistoryList.shift();
        }

        newHistoryList.push(_.cloneDeep(embodiment));

        this._updateModelData({
            historyIndex: newHistoryList.length - 1,
            history: newHistoryList,
        });
    }

    private _toggleHistory(offset: number): EmbodimentItem {
        const modelData = this.getModelData();
        const newIndex = modelData.historyIndex + offset;

        // 如果新索引超出边界，返回当前项
        if (this._isIndexOutOfBounds(newIndex)) {
            const currentItem = modelData.history[modelData.historyIndex];
            this._onToggleHistoryEmitter.fire(currentItem);
            return currentItem;
        }

        // 更新索引
        this._updateModelData({
            historyIndex: newIndex,
        });

        // 返回并触发事件
        const targetItem = modelData.history[newIndex];
        this._onToggleHistoryEmitter.fire(targetItem);
        return targetItem;
    }

    public toggleNextHistory(): EmbodimentItem {
        return this._toggleHistory(1);
    }

    public togglePrevHistory(): EmbodimentItem {
        return this._toggleHistory(-1);
    }

    public resetHistory(): void {
        this._updateModelData({
            historyIndex: 0,
            history: [],
        });
    }
}
